import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/shallow';
import { SceneDataElement } from '@/schemas/sceneDataSchema';
import { encodeFieldKey } from '@/schemas/validation/ValidationSchemaGenerator';

interface SceneStoreState {
  serverState: Record<string, SceneDataElement>;
  pendingChanges: Record<string, SceneDataElement['value']>;
  applySceneData: (sceneData: SceneDataElement[]) => void;
  mergeSceneData: (sceneData: SceneDataElement[], persistedKeys: string[]) => void;
  setValue: (key: string, value: SceneDataElement['value']) => void;
  setValues: (entries: [string, SceneDataElement['value']][]) => void;
  commitPending: () => void;
  reset: () => void;
}

function indexSceneData(sceneData: SceneDataElement[]): Record<string, SceneDataElement> {
  const indexed: Record<string, SceneDataElement> = {};
  for (const element of sceneData) {
    const key = encodeFieldKey(element);
    indexed[key] = element;
  }
  return indexed;
}

export const useSceneStore = create<SceneStoreState>()(
  devtools(
    (set) => ({
      serverState: {},
      pendingChanges: {},

      // Step load (GET/POST /quote): replace fields for incoming scopes and
      // clear pending for every returned key. Server is authoritative on step transitions.
      applySceneData: (sceneData) => {
        const indexed = indexSceneData(sceneData);
        const incomingScopes = new Set(sceneData.map((el) => el.scope));

        set(
          (current) => {
            // Keep fields from scopes NOT in the incoming data (other steps),
            // then overlay with the incoming fields. This removes stale entries
            // for deleted entities whose scope IS in the incoming data.
            const nextServerState = Object.fromEntries(
              Object.entries(current.serverState).filter(([, field]) => !incomingScopes.has(field.scope))
            );
            Object.assign(nextServerState, indexed);

            const nextPendingChanges = { ...current.pendingChanges };
            for (const key of Object.keys(indexed)) {
              delete nextPendingChanges[key];
            }

            return { serverState: nextServerState, pendingChanges: nextPendingChanges };
          },
          false,
          'applySceneData'
        );
      },

      // Persist-on-blur (PUT /steps/:step_id): replace fields for incoming scopes
      // but only clear pending for persistedKeys — the fields that were in the request.
      // This preserves in-flight edits to other fields that arrived while the request
      // was in-flight. Use applySceneData for step loads where no pending must survive.
      mergeSceneData: (sceneData, persistedKeys) => {
        const indexed = indexSceneData(sceneData);
        const persistedKeySet = new Set(persistedKeys);
        const incomingScopes = new Set(sceneData.map((el) => el.scope));

        set(
          (current) => {
            const nextServerState = Object.fromEntries(
              Object.entries(current.serverState).filter(([, field]) => !incomingScopes.has(field.scope))
            );
            Object.assign(nextServerState, indexed);

            const nextPendingChanges = { ...current.pendingChanges };
            for (const key of persistedKeySet) {
              delete nextPendingChanges[key];
            }

            return { serverState: nextServerState, pendingChanges: nextPendingChanges };
          },
          false,
          'mergeSceneData'
        );
      },

      // Pre-submit: fold pendingChanges into serverState and clear pending.
      // No-ops if there are no pending changes.
      commitPending: () =>
        set(
          (current) => {
            if (Object.keys(current.pendingChanges).length === 0) return current;

            const nextServerState = { ...current.serverState };
            for (const [key, value] of Object.entries(current.pendingChanges)) {
              if (nextServerState[key]) {
                nextServerState[key] = { ...nextServerState[key], value };
              }
            }
            return { serverState: nextServerState, pendingChanges: {} };
          },
          false,
          'commitPending'
        ),

      // Stage a single field edit in pendingChanges without touching serverState.
      setValue: (key, value) =>
        set((s) => ({ pendingChanges: { ...s.pendingChanges, [key]: value } }), false, `setValue:${key}`),

      // Batch version of setValue — one store update for multiple fields.
      setValues: (entries) =>
        set(
          (s) => {
            const nextPendingChanges = { ...s.pendingChanges };
            for (const [key, value] of entries) {
              nextPendingChanges[key] = value;
            }
            return { pendingChanges: nextPendingChanges };
          },
          false,
          'setValues'
        ),

      reset: () => set({ serverState: {}, pendingChanges: {} }, false, 'reset'),
    }),
    { name: 'SceneStore' }
  )
);

export function useSceneField(key: string): SceneDataElement | undefined {
  return useSceneStore(
    useShallow((s) => {
      const field = s.serverState[key];
      if (!field) return undefined;
      if (key in s.pendingChanges) return { ...field, value: s.pendingChanges[key] };
      return field;
    })
  );
}

export function useSceneFieldValue(key: string): SceneDataElement['value'] | undefined {
  return useSceneStore((s) => {
    if (key in s.pendingChanges) return s.pendingChanges[key];
    return s.serverState[key]?.value;
  });
}
